"use client";

import { ServicePageLayout, type ServicePageData } from "@/components/ServicePageLayout";
import { useTranslation } from "@/lib/i18n";
import { ETHERNET_L2VPN_SLA_PERCENT } from "@/lib/network-metrics";
import {
  leadTimeFor,
  formatLeadTimeDays,
} from "@/data/provisioning-lead-times";

const ucIcon = (d: string) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const heroIcon = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          </svg>
);

const UC_ICONS = ["M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7", "M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15", "M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0"].map((d) => ucIcon(d));

const COMMON = {
  accentColor: "#00d4aa",
  icon: heroIcon,
  ctaText: "",
  ctaService: "ethernet-l2vpn",
  bilingual: true,
} as const;

function dataEn(): ServicePageData {
  return {
    ...COMMON,
    title: "Ethernet / L2VPN",
    subtitle: "Connectivity Product",
    heroDescription:
      "Transparent Layer 2 connectivity over our MPLS backbone. Connect your sites with point-to-point or multipoint Ethernet services, full traffic isolation, and deterministic performance. Available from 10Mbps to 10Gbps across Southeast Europe.",
    specs: [
      { label: "Capacity", value: "10M–10G" },
      { label: "Service Types", value: "E-Line / E-LAN" },
      { label: "SLA Uptime", value: ETHERNET_L2VPN_SLA_PERCENT },
      { label: "Jitter", value: "<0.5ms" },
      { label: "Frame Size", value: "9000 MTU" },
      { label: "VLAN Support", value: "Q-in-Q" },
      { label: "Topology", value: "Any-to-Any" },
      { label: "Provisioning", value: formatLeadTimeDays(leadTimeFor("ethernet-l2vpn"), "en") },
    ],
    features: [
      {
        title: "Full Traffic Isolation",
        description:
          "Each L2VPN circuit is fully isolated on our MPLS backbone. Your traffic never shares forwarding tables with other customers — enterprise-grade security by design.",
      },
      {
        title: "Point-to-Point (E-Line)",
        description:
          "Dedicated Ethernet circuits between two locations with guaranteed bandwidth, low latency, and sub-millisecond jitter. Ideal for data center interconnects.",
      },
      {
        title: "Multipoint (E-LAN)",
        description:
          "Connect multiple sites in a single Layer 2 broadcast domain. Any-to-any connectivity without hub-and-spoke limitations.",
      },
      {
        title: "Jumbo Frame Support",
        description:
          "9000-byte MTU support across the entire path for maximum throughput efficiency. No fragmentation on large data transfers.",
      },
      {
        title: "Flexible QoS Profiles",
        description:
          "Pre-built and custom QoS profiles for voice, video, and data traffic. CoS marking preserved end-to-end.",
      },
      {
        title: "SLA-Backed Performance",
        description:
          "Guaranteed latency, jitter, and packet loss parameters with financial SLA credits. Monitored 24/7 by our NOC.",
      },
    ],
    useCases: [
      {
        title: "Data Center Interconnect",
        description:
          "Low-latency, high-bandwidth Layer 2 links between primary and disaster recovery data centers. Supports live VM migration and synchronous replication.",
        icon: UC_ICONS[0],
      },
      {
        title: "Multi-Branch Networking",
        description:
          "Unified Layer 2 network across all office locations. Transparent LAN extension for applications that require broadcast domain connectivity.",
        icon: UC_ICONS[1],
      },
      {
        title: "Carrier Ethernet for ISPs",
        description:
          "Wholesale Layer 2 transport for ISPs and carriers who need transparent Ethernet connectivity between their PoPs without managing their own fiber.",
        icon: UC_ICONS[2],
      },
    ],
  };
}

/* Bulgarian copy translates the English page one to one. No figure is
 * added or changed. */
function dataBg(): ServicePageData {
  return {
    ...COMMON,
    title: "Ethernet / L2VPN",
    subtitle: "Услуга за свързаност",
    heroDescription:
      "Прозрачна свързаност на ниво 2 през нашия MPLS гръбнак. Свържете обектите си с Ethernet услуги точка до точка или многоточкови, с пълна изолация на трафика и предвидима производителност. Налично от 10 Mbps до 10 Gbps в Югоизточна Европа.",
    specs: [
      { label: "Капацитет", value: "10M–10G" },
      { label: "Видове услуги", value: "E-Line / E-LAN" },
      { label: "SLA за достъпност", value: ETHERNET_L2VPN_SLA_PERCENT },
      { label: "Джитер", value: "<0.5ms" },
      { label: "Размер на кадъра", value: "9000 MTU" },
      { label: "Поддръжка на VLAN", value: "Q-in-Q" },
      { label: "Топология", value: "Всеки с всеки" },
      { label: "Срок за доставка", value: formatLeadTimeDays(leadTimeFor("ethernet-l2vpn"), "bg") },
    ],
    features: [
      {
        title: "Пълна изолация на трафика",
        description:
          "Всяка L2VPN верига е напълно изолирана в нашия MPLS гръбнак. Трафикът ви никога не споделя таблици за препращане с други клиенти. Сигурността е заложена в самата архитектура.",
      },
      {
        title: "Точка до точка (E-Line)",
        description:
          "Специализирани Ethernet вериги между две локации с гарантирана пропускателна способност, ниска латентност и джитер под милисекунда. Подходящо за връзки между центрове за данни.",
      },
      {
        title: "Многоточкова (E-LAN)",
        description:
          "Свържете няколко обекта в един широкоразпръсквателен домейн на ниво 2. Свързаност всеки с всеки, без ограниченията на схемата звезда.",
      },
      {
        title: "Поддръжка на големи кадри",
        description:
          "MTU от 9000 байта по целия път за максимална ефективност на преноса. Без фрагментиране при големи прехвърляния на данни.",
      },
      {
        title: "Гъвкави профили за QoS",
        description:
          "Готови и персонализирани профили за QoS за глас, видео и данни. Маркировката CoS се запазва от край до край.",
      },
      {
        title: "Производителност, гарантирана с SLA",
        description:
          "Гарантирани параметри за латентност, джитер и загуба на пакети с финансови кредити по SLA. Наблюдение 24/7 от нашия NOC.",
      },
    ],
    useCases: [
      {
        title: "Връзки между центрове за данни",
        description:
          "Връзки на ниво 2 с ниска латентност и висок капацитет между основния и резервния център за данни. Поддържат миграция на работещи виртуални машини и синхронна репликация.",
        icon: UC_ICONS[0],
      },
      {
        title: "Мрежа между клонове",
        description:
          "Обща мрежа на ниво 2 за всички офиси. Прозрачно разширение на локалната мрежа за приложения, които изискват общ широкоразпръсквателен домейн.",
        icon: UC_ICONS[1],
      },
      {
        title: "Операторски Ethernet за интернет доставчици",
        description:
          "Пренос на едро на ниво 2 за интернет доставчици и оператори, които се нуждаят от прозрачна Ethernet свързаност между своите точки на присъствие, без да поддържат собствено влакно.",
        icon: UC_ICONS[2],
      },
    ],
  };
}

export function EthernetL2vpnClient() {
  const { language } = useTranslation();
  return <ServicePageLayout data={language === "bg" ? dataBg() : dataEn()} />;
}
